import { useEffect, useRef, useState } from "react";
import { Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton, Box, RadioGroup, Radio, Textarea, Flex, FormControl, FormLabel, Input, FormErrorMessage, Button, Text, Divider, Spinner } from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateAwardModel, createAwardSchema, Employee, initialValues, Props, validityOption } from "./modal";
import { useFetch } from "../../../../hooks/useFetch";
import { CustomToast } from "../../../../components";
import { HrlenseInput } from "../../../../components/Input/Input";
import CustomButton from "../../../../components/Inputs/Button/customButton";
import MyDiv from "./createNewAward.style";
import { Dropdown } from "../../../../components/DropDown/DropDown";
import { format } from "date-fns/format";

const LocalEmployeeTypeahead = ({ control, name, options, loading, onSearch, disabled }: {
    control: any;
    name: string;
    options: Employee[];
    loading: boolean;
    onSearch: (q?: string) => void;
    disabled?: boolean;
}) => {

    const [showOptions, setShowOptions] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const wrapperRef = useRef<any>(null);

    useEffect(() => {
        const handleClickOutside = (e: any) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowOptions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {

                const selected = field.value;

                return (
                    <Box ref={wrapperRef} position="relative">
                        <Input
                            value={
                                selected
                                    ? selected.employee_Name
                                    : inputValue
                            }
                            placeholder="Search Employee..."
                            onChange={(e) => {
                                const val = e.target.value;
                                setInputValue(val);
                                field.onChange(null);
                                onSearch(val);
                                setShowOptions(true);
                            }}
                            onFocus={() => setShowOptions(true)}
                            disabled={disabled}
                        />

                        {loading && (
                            <Spinner size="sm" position="absolute" right="10px" top="10px" />
                        )}

                        {showOptions && options.length > 0 && (
                            <Box position="absolute" bg="white" borderWidth="1px" borderRadius="md" mt={1} w="100%" maxH="200px" overflowY="auto" zIndex={20}>
                                {options.map((emp) => (
                                    <Box
                                        key={emp.employeeId}
                                        px={3}
                                        py={2}
                                        _hover={{ bg: "gray.100", cursor: "pointer" }}
                                        onClick={() => {
                                            field.onChange(emp);
                                            setInputValue("");
                                            setShowOptions(false);
                                        }}
                                    >
                                        {emp.employee_Name}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                );
            }}
        />
    );
};

const CreateNewAward = ({ isOpen, onClose, refresh, editData }: Props) => {
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const [categories, setCategories] = useState<any[]>([]);
    const [companies, setCompanies] = useState<any[]>([]);
    const [branch, setBranch] = useState<any[]>([]);
    const [department, setDepartment] = useState<any[]>([]);
    const [employeeOptions, setEmployeeOptions] = useState<Employee[]>([]);
    const managerTimeout = useRef<any>(null);

    const isEdit = !!editData;
    const { control, handleSubmit, watch, formState: { errors }, reset, setValue } = useForm<CreateAwardModel>({
        resolver: yupResolver(createAwardSchema(isEdit)) as any,
        defaultValues: initialValues,
    });

    const selectedDepartment = watch("department");
    const selectedBranch = watch("branch");
    const selectedCompany = watch("company");
    const isEmployeeDisabled =
        !selectedCompany || !selectedBranch || !selectedDepartment;


    const onSubmit = async (data: CreateAwardModel) => {
        const emp = data.employeeId;

        let isEmployeeValid = false;

        if (typeof emp === "number") {
            isEmployeeValid = emp > 0;
        } else if (typeof emp === "object" && emp !== null) {
            isEmployeeValid = emp.employeeId > 0;
        }

        if (!isEmployeeValid) {
            addToast({ message: "Please select an Employee", status: "error" });
            return;
        }

        const formData = new FormData();
        formData.append("title", data.title);
        formData.append("type", data.type);
        formData.append("category", data.category.toString());
        formData.append("validity", data.validity);
        formData.append("description", data.description || "");

        formData.append("company", String(data.company));
        formData.append("branch", String(data.branch));
        formData.append("department", String(data.department));
        formData.append("issuedDate", format(data.issuedDate, "yyyy-MM-dd"));
        if (data.employeeId) {
            const empId = typeof data.employeeId === "object" ? data.employeeId.employeeId : data.employeeId;

            formData.append("recipients", String(empId));
        }

        if (data.template) formData.append("file", data.template);

        const url = editData?.awardId ? `Hrlense_Awards/updateaward/${editData.awardId}` : "Hrlense_Awards/createaward";
        const method = editData?.awardId ? "PUT" : "POST";
        const message = editData?.awardId ? "Award Updated Successfully" : "Award Created Successfully";

        const res = await fetchApi(url, method, formData, null, message, true);
        if (res) {
            reset();
            onClose();
            refresh();
        }
    };

    const handleCancel = () => {
        reset();
        onClose();
    };

    useEffect(() => {
        const fetchData = async () => {
            const companiesRes = await fetchApi("Dropdown/roleWiseCompany", "GET");
            const departmentRes = await fetchApi("DropDown/department", "GET");
            const categoryRes = await fetchApi("Hrlense_Awards/categories-dropdown", "GET",);

            if (companiesRes) setCompanies(companiesRes);
            if (departmentRes) setDepartment(departmentRes);
            if (categoryRes) setCategories(categoryRes);
        };
        fetchData();
    }, []);

    const fetchBranches = async (companyId: number) => {
        const res = await fetchApi("DropDown/roleWiseBranch", "GET", null, `companyId=${companyId}`);
        setBranch(res || []);
    };

    const fetchEmployees = async (branchIds?: number[], departmentId?: number, filter?: string) => {
        const query = `branchIds=${branchIds?.join(",") || ""}&departmentId=${departmentId ?? ""}${filter ? `&filter=${filter}` : ""}`;
        const res = await fetchApi("Dropdown/EmployeeListBranchDepartmentWise", "GET", null, query);

        setEmployeeOptions(res || []);
    };


    const handleEmployeeChange = (value?: string) => {
        if (managerTimeout.current) clearTimeout(managerTimeout.current);

        managerTimeout.current = setTimeout(async () => {
            if (!value) return;
            await fetchEmployees(
                watch("branch") ? [Number(watch("branch"))] : [],
                Number(watch("department")),
                value,
            );
        }, 500);
    };

    useEffect(() => {
        if (!isOpen) return;

        if (editData) {
            reset({
                title: editData.title || "",
                type: editData.type || "Award",
                category: editData.categoryId || undefined,
                validity: editData.validityType || "Monthly",
                description: editData.description || "",
                issuedDate: editData.issuedDate || "",
                company: editData.companyId || undefined,
                branch: editData.branchId || undefined,
                department: editData.departmentId || undefined,
                employeeId: editData.employeeId
                    ? {
                        employeeId: editData.employeeId,
                        employee_Name: editData.employeeName || "",
                    }
                    : undefined,
                // employeeId: editData.employeeId || 0,
            });

            if (editData.companyId) fetchBranches(editData.companyId);
            if (editData.branchId && editData.departmentId)
                fetchEmployees([editData.branchId], editData.departmentId);
        } else {
            reset({
                title: "",
                type: "Award",
                category: 0,
                validity: "Monthly",
                description: "",
                company: undefined,
                branch: undefined,
                department: undefined,
                employeeId: 0,
            });
        }
    }, [isOpen, editData, reset]);

    useEffect(() => {
        const subscription = watch((value, { name }) => {

            if (name === "company" && value.company) {
                fetchBranches(value.company);
                setValue("employeeId", null);
                setEmployeeOptions([]);
            }

            if (
                (name === "branch" || name === "department") &&
                value.branch &&
                value.department
            ) {
                fetchEmployees([value.branch], value.department);
                setValue("employeeId", null);
            }

        });

        return () => subscription.unsubscribe();
    }, [watch]);



    return (
        <MyDiv>
            <Drawer isOpen={isOpen} placement="right" onClose={handleCancel} size="md" >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader>{isEdit ? "Update Award / Certificate" : "Create Award / Certificate"}</DrawerHeader>
                    <Divider />

                    <DrawerBody>
                        <Box py={2}>
                            <HrlenseInput isRequired label="Title" name="title" maxLength={100} control={control} errors={errors} />

                            <FormControl mt={2} isInvalid={!!errors.type}>
                                <FormLabel>Type</FormLabel>
                                <Controller name="type" control={control}
                                    render={({ field }) => (
                                        <RadioGroup {...field}>
                                            <Flex gap={4}>
                                                <Radio value="Award">Award</Radio>
                                                <Radio value="Certificate">Certificate</Radio>
                                            </Flex>
                                        </RadioGroup>
                                    )}
                                />
                                <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
                            </FormControl>

                            <Box mt={2}>
                                <Dropdown isRequired label="Validity" name="validity" control={control} options={validityOption} labelKey="lable" valueKey="lable" errors={errors} />
                            </Box>

                            <Box mt={2}>
                                <Dropdown isRequired label="Category" name="category" control={control} options={categories} labelKey="name" valueKey="id" errors={errors} />
                            </Box>

                            <Box mt={2}>
                                <HrlenseInput type="Date" isRequired name="issuedDate" label="Issued Date" control={control} placeholder="Enter Issue Date" errors={errors} />
                            </Box>

                            <FormControl mt={4} isInvalid={!!errors.template}>
                                <FormLabel>Upload Template (PDF only, Max 5MB)</FormLabel>

                                <Controller
                                    name="template"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="file"
                                            accept="application/pdf"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];

                                                if (!file) {
                                                    field.onChange(null);
                                                    return;
                                                }

                                                if (file.type !== "application/pdf") {
                                                    addToast({
                                                        message: "Only PDF files are allowed",
                                                        status: "error",
                                                    });
                                                    e.target.value = "";
                                                    return;
                                                }

                                                const maxSize = 5 * 1024 * 1024;

                                                if (file.size > maxSize) {
                                                    addToast({
                                                        message: "File size must be less than 5MB",
                                                        status: "error",
                                                    });
                                                    e.target.value = "";
                                                    return;
                                                }

                                                field.onChange(file);
                                            }}
                                        />
                                    )}
                                />

                                <FormErrorMessage>
                                    {errors.template?.message}
                                </FormErrorMessage>
                            </FormControl>

                            <Box mt={2}>
                                <Dropdown isRequired label="Company" name="company" control={control} options={companies} labelKey="company_Name" valueKey="company_ID" errors={errors} />
                            </Box>

                            <Box mt={2}>
                                <Dropdown isRequired label="Branch" name="branch" control={control} options={branch} labelKey="branch_Name" valueKey="branch_Id" errors={errors} />
                            </Box>

                            <Box mt={2}>
                                <Dropdown isRequired label="Department" name="department" control={control} options={department} labelKey="departmentName" valueKey="id" errors={errors} />
                            </Box>

                            <FormControl mt={4} isInvalid={!!errors.employeeId}>
                                <FormLabel> Employee <Text as="span" color="red.500">*</Text> </FormLabel>
                                <LocalEmployeeTypeahead
                                    name="employeeId"
                                    control={control}
                                    options={employeeOptions}
                                    loading={loading}
                                    onSearch={handleEmployeeChange}
                                    disabled={isEmployeeDisabled}
                                />

                                <FormErrorMessage> {errors.employeeId?.message} </FormErrorMessage>
                            </FormControl>

                            <FormControl mt={4} isInvalid={!!errors.description}>
                                <FormLabel> Description <span style={{ color: "red" }}>*</span> </FormLabel>
                                <Controller name="description" control={control} render={({ field }) => <Textarea {...field} />} />
                                <FormErrorMessage>
                                    {errors.description?.message}
                                </FormErrorMessage>
                            </FormControl>
                        </Box>
                    </DrawerBody>

                    <Flex justify="flex-end" mb={5} gap={3} mr={2}>
                        <Button colorScheme="green" className="font-poppins text_md text_medium " onClick={handleSubmit(onSubmit)} isLoading={loading}>
                            {isEdit ? "Update" : "Submit"}
                        </Button>
                        <CustomButton title="Cancel" onClick={handleCancel} />
                    </Flex>
                </DrawerContent>
            </Drawer>
        </MyDiv >
    );
};

export default CreateNewAward;
